import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";

/**
 * ============================================================
 * PROFIL CONTEXT — GESTION UTILISATEUR & AUTHENTIFICATION
 * ============================================================
 *
 * Rôle :
 * - centraliser l'état utilisateur (profil)
 * - gérer l'authentification (login / logout)
 * - exposer les rôles (admin / collaborateur / client)
 * - fournir les infos de pricing
 *
 * IMPORTANT :
 * 👉 Toute la logique de session doit passer par ici
 * 👉 Le logout est centralisé et fiable (fix du bug actuel)
 * ============================================================
 */

export const ProfilContext = createContext(null);

export const ProfilProvider = ({ children }) => {
  // ============================================================================
  // 1) ÉTATS PRINCIPAUX
  // ============================================================================
  const [userProfil, setUserProfilState] = useState(null);

  /**
   * authStatus :
   * - "loading"   → boot de l'app
   * - "connected" → utilisateur connecté
   * - "visitor"   → utilisateur non connecté
   */
  const [authStatus, setAuthStatus] = useState("loading");

  // ============================================================================
  // 2) INITIALISATION SESSION (LOCAL STORAGE)
  // ============================================================================
  useEffect(() => {
    /**
     * ==========================================================
     * GUIDE — RESTAURATION SESSION
     * ==========================================================
     *
     * On restaure la session depuis localStorage.
     * Si données corrompues → nettoyage automatique.
     */
    const initProfil = () => {
      const savedUser = localStorage.getItem("user_data");

      if (savedUser) {
        try {
          const parsed = JSON.parse(savedUser);

          setUserProfilState(parsed);
          setAuthStatus("connected");
        } catch (e) {
          console.warn("Profil corrompu, reset session");

          localStorage.removeItem("user_data");
          localStorage.removeItem("user_token");

          setUserProfilState(null);
          setAuthStatus("visitor");
        }
      } else {
        setUserProfilState(null);
        setAuthStatus("visitor");
      }
    };

    initProfil();
  }, []);

  // ============================================================================
  // 3) ACTION PRINCIPALE — SET PROFIL (LOGIN / LOGOUT)
  // ============================================================================
  const updateProfilManuellement = useCallback((userData) => {
    /**
     * ==========================================================
     * GUIDE — LOGIN
     * ==========================================================
     */
    if (userData) {
      setUserProfilState(userData);
      localStorage.setItem("user_data", JSON.stringify(userData));
      setAuthStatus("connected");
      return;
    }

    /**
     * ==========================================================
     * GUIDE — LOGOUT (CRITIQUE)
     * ==========================================================
     *
     * Ce bloc est LA source de vérité pour la déconnexion.
     * Il doit :
     * - supprimer les données locales
     * - reset React state
     * - passer en mode "visitor"
     */
    setUserProfilState(null);

    localStorage.removeItem("user_data");
    localStorage.removeItem("user_token");

    setAuthStatus("visitor");
  }, []);

  // ============================================================================
  // 4) LOGOUT PUBLIC (FIX BUG)
  // ============================================================================
  /**
   * ==========================================================
   * GUIDE — LOGOUT EXPOSÉ AU RESTE DE L'APP
   * ==========================================================
   *
   * ⚠️ C’EST CE QUI MANQUAIT → BUG LOGOUT
   *
   * Avant :
   * - App.js appelait logout()
   * - MAIS logout n'existait pas → rien ne se passait
   *
   * Maintenant :
   * - logout appelle updateProfilManuellement(null)
   * - donc déconnexion réelle
   */
  const logout = useCallback(() => {
    updateProfilManuellement(null);
  }, [updateProfilManuellement]);

  // ============================================================================
  // 5) PROFIL PRICING (ROLES + REMISES)
  // ============================================================================
  const profilPricing = useMemo(() => {
    if (!userProfil) return null;

    const type = (userProfil?.type_profil || "").toLowerCase();

    const isAdmin = !!(userProfil.isAdmin || type === "admin");

    const isCollaborator = !!(
      userProfil.isCollaborator ||
      type === "collaborator" ||
      type === "collaborateur"
    );

    return {
      isAdmin,
      isCollaborator,
      type_profil: userProfil.type_profil || "client",
      discountRate: parseInt(userProfil.discount || 0, 10),
    };
  }, [userProfil]);

  // ============================================================================
  // 6) ACCÈS TARIFS MEMBRES
  // ============================================================================
  const canAccessMemberPricing = useMemo(() => {
    const hasId = !!(userProfil?.id || userProfil?.uid);
    const type = (userProfil?.type_profil || "").toLowerCase();

    const isMember =
      type === "client" ||
      type === "admin" ||
      type === "collaborateur" ||
      type === "collaborator";

    return authStatus === "connected" && hasId && isMember;
  }, [authStatus, userProfil]);

  // ============================================================================
  // 7) VALEURS EXPOSÉES AU CONTEXT
  // ============================================================================
  const value = {
    userProfil,
    profilPricing,

    /**
     * setter principal (login/logout interne)
     */
    setUserProfil: updateProfilManuellement,

    /**
     * 🔥 FIX CRITIQUE
     */
    logout,

    authStatus,
    isAuthenticated: authStatus === "connected",
    canAccessMemberPricing,
  };

  return (
    <ProfilContext.Provider value={value}>{children}</ProfilContext.Provider>
  );
};

// ============================================================================
/**
 * HOOK CUSTOM
 * Simplifie l'accès au context
 */
// ============================================================================
export const useProfil = () => {
  const context = useContext(ProfilContext);

  if (!context) {
    throw new Error("useProfil must be used within a ProfilProvider");
  }

  return context;
};
